import React, { useEffect, useRef, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import { SkeletonBlock } from "./SkeletonUI";

/* ─── Platform config ────────────────────────────────────────────────────── */
const PLATFORMS = {
  linkedin: {
    label: "LinkedIn",
    icon: "bi-linkedin",
    color: "#0A66C2",
    followUrl: "https://www.linkedin.com/company/enterprise-computing-ltd",
  },
  instagram: {
    label: "Instagram",
    icon: "bi-instagram",
    color: "#C13584",
    followUrl: "https://www.instagram.com/enterprisecomputingltd",
  },
  youtube: {
    label: "YouTube",
    icon: "bi-youtube",
    color: "#FF0000",
    followUrl: "https://www.youtube.com/@enterprisecomputingltd",
  },
};

/*
 * ─── STRAPI CONTENT TYPE: ecl-social-posts ───────────────────────────────
 *
 * Fields to create in Strapi:
 *   platform  – Short text  (linkedin | instagram | youtube)
 *   embedSrc  – Long text   (full iframe src URL)
 *   title     – Short text  (used as iframe title for accessibility)
 *   isVideo   – Boolean     (true for YouTube — renders at 16:9 height)
 *
 * LinkedIn  → embedSrc: https://www.linkedin.com/embed/feed/update/urn:li:share:XXXXX
 * Instagram → embedSrc: https://www.instagram.com/p/SHORT_CODE/embed/
 * YouTube   → embedSrc: https://www.youtube.com/embed/VIDEO_ID  (isVideo: true)
 */

const TABS = [
  { key: "all",       label: "All",       icon: "bi-grid-3x3-gap-fill" },
  { key: "linkedin",  label: "LinkedIn",  icon: "bi-linkedin"           },
  { key: "instagram", label: "Instagram", icon: "bi-instagram"          },
  { key: "youtube",   label: "YouTube",   icon: "bi-youtube"            },
];

const isPlaceholder = (src) => src.includes("YOUR_");

/* ─── Placeholder card body ──────────────────────────────────────────────── */
function PlaceholderEmbed({ platform }) {
  const meta = PLATFORMS[platform];
  return (
    <div className="sf-placeholder">
      <i className={`bi ${meta.icon} sf-placeholder__icon`} style={{ color: meta.color }} />
      <p className="sf-placeholder__title">Add your {meta.label} embed</p>
      <p className="sf-placeholder__hint">
        Replace <code>YOUR_…_ID</code> in <code>SocialFeed.js</code> with a real post ID.
      </p>
    </div>
  );
}

/* ─── Single embed card ──────────────────────────────────────────────────── */
function SocialCard({ card }) {
  const meta    = PLATFORMS[card.platform];
  const isPh    = isPlaceholder(card.embedSrc);
  const iframeH = card.isVideo ? 215 : 480;

  return (
    <div className="sf-card">
      {/* Card header */}
      <div className="sf-card__header" style={{ borderTopColor: meta.color }}>
        <span className="sf-card__platform">
          <i className={`bi ${meta.icon}`} style={{ color: meta.color }} />
          <span>{meta.label}</span>
        </span>
        <a
          href={meta.followUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="sf-card__follow"
          style={{ color: meta.color }}
        >
          Follow
          <i className="bi bi-arrow-up-right ms-1" style={{ fontSize: "0.7rem" }} />
        </a>
      </div>

      {/* Card body — iframe or placeholder */}
      <div className="sf-card__body" style={{ height: iframeH }}>
        {isPh ? (
          <PlaceholderEmbed platform={card.platform} />
        ) : (
          <iframe
            src={card.embedSrc}
            title={card.title}
            loading="lazy"
            allowFullScreen
            allow={card.isVideo ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" : undefined}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Skeleton row ───────────────────────────────────────────────────────── */
function SocialFeedSkeleton() {
  return (
    <div className="sf-scroll" style={{ pointerEvents: "none" }}>
      {[1, 2, 3].map(n => (
        <div key={n} className="sf-card">
          <div className="sf-card__header" style={{ borderTopColor: "#e5e7eb" }}>
            <SkeletonBlock width={100} height={14} radius={6} />
            <SkeletonBlock width={48} height={14} radius={6} />
          </div>
          <div className="sf-card__body" style={{ height: 480 }}>
            <SkeletonBlock width="100%" height="100%" radius={0} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Main section ───────────────────────────────────────────────────────── */
export default function SocialFeed() {
  const [activeTab, setActiveTab] = useState("all");
  const [cards, setCards]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchFromStrapi("/ecl-social-posts")
      .then(res => {
        const items = res?.data || [];
        setCards(
          items.map(item => ({
            id:       item.id,
            platform: item.attributes.platform,
            embedSrc: item.attributes.embedSrc,
            title:    item.attributes.title,
            isVideo:  item.attributes.isVideo ?? false,
          }))
        );
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeTab === "all"
    ? cards
    : cards.filter(c => c.platform === activeTab);

  function scroll(dir) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 380, behavior: "smooth" });
  }

  return (
    <section className="sf-section">
      <div className="container">
        {/* Heading */}
        <div className="text-center mb-4">
          <p
            className="text-uppercase fw-semibold mb-1"
            style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
          >
            Stay connected
          </p>
          <h2
            className="fw-bold"
            style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}
          >
            Follow us on social
          </h2>
          <p className="text-muted mt-2" style={{ maxWidth: 440, margin: "0.5rem auto 0" }}>
            Get the latest news, updates, and stories from ECL across our channels.
          </p>
        </div>

        {/* Platform filter tabs */}
        <div className="sf-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`sf-tab${activeTab === tab.key ? " sf-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
              style={
                activeTab === tab.key && tab.key !== "all"
                  ? { background: PLATFORMS[tab.key].color, borderColor: PLATFORMS[tab.key].color, color: "#fff" }
                  : {}
              }
            >
              <i className={`bi ${tab.icon}`} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Scroll row + arrow controls */}
        {loading ? (
          <SocialFeedSkeleton />
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted py-4" style={{ fontSize: "0.9rem" }}>
            No posts available{activeTab !== "all" ? ` for ${PLATFORMS[activeTab]?.label}` : ""}.
          </p>
        ) : (
          <div className="sf-scroll-wrapper">
            <button
              className="sf-arrow sf-arrow--left"
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
            >
              <i className="bi bi-chevron-left" />
            </button>

            <div className="sf-scroll" ref={scrollRef}>
              {filtered.map(card => (
                <SocialCard key={card.id} card={card} />
              ))}
            </div>

            <button
              className="sf-arrow sf-arrow--right"
              onClick={() => scroll(1)}
              aria-label="Scroll right"
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}

        {/* Platform follow strip */}
        <div className="sf-follow-strip">
          {Object.entries(PLATFORMS).map(([key, meta]) => (
            <a
              key={key}
              href={meta.followUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sf-follow-link"
              style={{ "--platform-color": meta.color }}
            >
              <i className={`bi ${meta.icon}`} />
              <span>{meta.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
